import type { NewEpisodeCard } from "@anisync/types";
import { SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { getNewEpisodes } from "../lib/api";
import { fadeIn, stagger } from "../lib/motion";
import { Badge } from "./ui/badge";

/**
 * Półka „Nowe odcinki".
 *
 * Pokazuje wyłącznie to, co da się obejrzeć: kafel powstaje dopiero, gdy któreś
 * włączone źródło faktycznie wystawiło odcinek. Wersja oparta na dacie emisji
 * z AniLista byłaby o kilka dni wcześniejsza od materiału — czyli przez kilka dni
 * prowadziłaby na stronę tytułu, na której nie ma czego włączyć. Plakietka, która
 * dwa razy skłamie, przestaje być czytana.
 *
 * Nad „Kontynuuj oglądanie", bo odpowiada na inne pytanie i na świeższe:
 * tamta półka mówi „wróć do tego, co zacząłeś", ta — „coś się właśnie pojawiło".
 */
export function NewEpisodesRail({ enabled }: { enabled: boolean }) {
  const [items, setItems] = useState<NewEpisodeCard[] | null>(null);

  useEffect(() => {
    if (!enabled) {
      setItems(null);
      return;
    }
    let cancelled = false;
    getNewEpisodes(12)
      .then((data) => {
        if (!cancelled) setItems(data.items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // Brak nowych odcinków to stan normalny i najczęstszy — poza tygodniem emisji
  // ta półka po prostu nie istnieje. Nagłówek nad pustką sugerowałby awarię.
  if (!enabled || items === null || items.length === 0) return null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <SparklesIcon className="size-5 text-accent" />
        Nowe odcinki
        <Badge variant="accent">{items.length}</Badge>
      </h2>
      <ul className="tile-grid gap-4 [--tile-min:11rem]">
        {items.map((card, i) => (
          <li key={card.titleId} className="enter-rise" style={stagger(i)}>
            <NewEpisodeTile card={card} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function NewEpisodeTile({ card }: { card: NewEpisodeCard }) {
  const name = card.title.titleEnglish ?? card.title.titleRomaji;

  // Gotowy materiał otwieramy wprost w odtwarzaczu; inaczej prowadzimy na stronę
  // tytułu, gdzie start odcinka uruchamia pobieranie i pokazuje jego postęp.
  const to =
    card.cachedMediaFileId !== null
      ? `/odtwarzacz/${card.cachedMediaFileId}?tytul=${card.titleId}&odcinek=${card.episodeNumber}`
      : `/tytul/${card.titleId}`;

  return (
    <Link
      to={to}
      className="focus-ring-own group flex flex-col gap-2 rounded-xl border border-accent/40 bg-card p-3 outline-none transition-all duration-200 ease-tv hover:border-accent hover:bg-secondary/40 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
    >
      <div className="relative overflow-hidden rounded-lg bg-secondary">
        {card.title.coverUrl !== null ? (
          <img
            {...fadeIn}
            src={card.title.coverUrl}
            alt=""
            loading="lazy"
            className="aspect-[2/3] w-full object-cover"
          />
        ) : (
          <div className="aspect-[2/3] w-full" />
        )}
        {/*
          Plakietka NA okładce, nie pod nią. Rzecz w tym, żeby dało się ją
          zauważyć, przewijając stronę wzrokiem — podpis w wierszu tekstu pod
          kaflem czyta się dopiero po zatrzymaniu się na tym kaflu.
        */}
        <span className="absolute top-2 left-2 rounded-md bg-accent px-2 py-0.5 text-xs font-semibold text-accent-foreground shadow-lg">
          NOWY
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="line-clamp-2 text-sm font-medium">{name}</span>
        <span className="text-xs text-muted-foreground">
          Odcinek {card.episodeNumber}
          {card.moreCount > 0 && ` · +${card.moreCount} więcej`}
        </span>

        {/*
          Język i zapasowość mówią, czego spodziewać się po napisach — to ta sama
          para pól, którą siatka odcinków pokazuje na stronie tytułu. Widz ma to
          wiedzieć PRZED pobraniem kilkuset megabajtów.
        */}
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant={card.language === "pl" ? "success" : "outline"}>
            {card.language === "pl" ? "PL" : "EN"}
          </Badge>
          {card.fallback && <Badge variant="warning">zapas</Badge>}
          <span className="truncate text-xs text-muted-foreground">{card.sourceLabel}</span>
        </div>
      </div>
    </Link>
  );
}
